import Hr from '@creator/ui/components/Hr/Hr';
import Image from '@creator/ui/components/Image/Image';
import Text from '@creator/ui/components/Text/Text';
import UserDisplayName from '@creator/ui/components/UserDisplayName/UserDisplayName';
import { convertFirebaseTimestampToDate, formatDate } from '@creator/ui/utils/date-utils';
import { cn } from '@creator/ui/utils/ui';
import Icon from '@src/basic-components/Icon/Icon';
import ProfileImage from '@src/components/ProfileImage/ProfileImage';
import { ensureTokenUrl } from '@src/model/config/helpers';
import { getToken, getTokenIcon } from '@src/model/token/helpers';
import { ensurePostUrl, isPostDeleted } from '@src/model/upvote/helpers';
import { canViewPendingContent } from '@src/model/upvote/permission';
import { PostStatus } from '@src/store/contentEntities/model';
import { usePost } from '@src/store/posts/hooks/usePost';
import { usePostImage } from '@src/store/posts/hooks/usePostImage';
import { propsEqualityFn } from '@src/utils/object-utils/object-utils';
import { type FC, Fragment, memo, type ReactNode, useMemo } from 'react';
import { Link } from 'react-router';
import PendingPostApproveDelete from '../PendingPostApproveDelete/PendingPostApproveDelete';
import PinnedIndicator from '../PinnedIndicator/PinnedIndicator';
import PostMenuPopover from '../PostMenuPopover/PostMenuPopover';
import PostStatsAndReactions from '../PostStatsAndReactions/PostStatsAndReactions';

export interface PostCardCompactProps {
    className?: string;
    tokenName: string;
    postId: string;
    children?: ReactNode;
    hideProfileImage?: boolean;
    urlTo?: 'view' | 'edit';
}

const PostCardCompact: FC<PostCardCompactProps> = props => {
    const { tokenName, postId, className, urlTo = 'view' } = props;

    const post = usePost(postId);
    const isDeleted = isPostDeleted(postId);

    const { url: postImageUrl } = usePostImage({ postId, imageType: 'featured' });
    const isLoading = !post;

    const url = useMemo(getPostUrl, []);

    function getPostUrl() {
        if (urlTo === 'edit') {
            // if (post?.status === PostStatus.DRAFT) return `/bbs/${tokenName}/posts/create/${postId}`;
            return `/bbs/${tokenName}/posts/create/${postId}`;
        }
        return `/${ensureTokenUrl(tokenName)}/${ensurePostUrl(postId)}`;
    }

    function getPostCreatedTime() {
        if (!post) return '';
        const { createdAt } = post;
        const _date = convertFirebaseTimestampToDate(createdAt);
        return formatDate(_date);
    }

    function getPostScheduledTime() {
        if (!post) return '';
        const { scheduledAt } = post;
        if (!scheduledAt) return '';
        const _date = convertFirebaseTimestampToDate(scheduledAt);
        return formatDate(_date);
    }

    const renderPostCardCompactFooter = () => {
        return (
            <div className="flex items-center h-5 text-gray-500">
                <PostStatsAndReactions className="space-x-2" tokenName={tokenName} isDisabled={isPostDeleted(postId)} postId={postId} />
                <PostMenuPopover postId={postId} tokenName={tokenName} />
            </div>
        );
    };

    function renderTime() {
        if (!post) return null;
        const { status } = post;
        if (status === PostStatus.PUBLISHED_SCHEDULED) {
            return (
                <div className="flex items-center space-x-1">
                    <Icon name="scheduleClock" className="text-base" />
                    <Text className="">{getPostScheduledTime()}</Text>
                </div>
            );
        }

        if (status === PostStatus.DRAFT) {
            return (
                <div className="flex items-center space-x-1">
                    <Icon name="post" className="text-base" />
                    <Text className="">{getPostCreatedTime()}</Text>
                </div>
            );
        }

        return <Text className="">{getPostCreatedTime()}</Text>;
    }

    function renderByline() {
        if (!post || isLoading) return <Text isLoading={isLoading} className="flex-1 text-xs" />;
        const { publisherName, isPinned } = post;

        return (
            <div className="flex items-center flex-1 space-x-2 text-xs">
                <UserDisplayName isLoading={isLoading} displayName={publisherName} />
                <div className="w-[1px] bg-gray-950 dark:bg-gray-50 h-3 shrink-0" />
                {renderTime()}
                {isPinned && (
                    <>
                        <div className="w-[1px] bg-gray-950 dark:bg-gray-50 h-3 shrink-0" />
                        <PinnedIndicator labelClassName="hidden" />
                    </>
                )}
            </div>
        );
    }

    function renderPostTitle() {
        return (
            <Text isLoading={isLoading} enableMultiline={true} as="h3" className="flex-1 h-10 text-sm font-medium line-clamp-2">
                {post?.title}
            </Text>
        );
    }

    function renderProfileImage() {
        const { hideProfileImage } = props;
        if (hideProfileImage) return null;
        return <ProfileImage isLoading={isLoading} tokenName={tokenName} className="w-11 h-11 lg:w-14 lg:h-14" displayName={post?.publisherName || ''} />;
    }

    function renderMainImage() {
        const token = getToken(tokenName); // deprecated we should use bunny only
        const fallbackImage = token?.imageURI || getTokenIcon(tokenName);

        if (!postImageUrl) return <Image isLoading={isLoading} fit="default" src={token?.imageURI} className="object-cover w-11 h-11 lg:w-14 lg:h-14" />;
        return <Image isLoading={isLoading} fit="default" className="object-cover w-11 h-11 lg:w-14 lg:h-14" src={postImageUrl} fallbackSrc={fallbackImage} />;
    }

    function renderPendingApproveOrDelete() {
        if (post?.status !== PostStatus.PENDING) return null;
        if (!canViewPendingContent(tokenName)) return null;

        return (
            <Fragment>
                <PendingPostApproveDelete className="justify-center mt-4" postId={postId} />
                <Hr className="my-4" />
            </Fragment>
        );
    }

    function renderFooter(className?: string) {
        return (
            <div className={cn('flex mt-1 items-center', className)}>
                {renderByline()}
                {renderPostCardCompactFooter()}
            </div>
        );
    }

    return (
        <Link to={url} data-testid="PostCardCompact" data-post-id={postId} className={cn('', className)}>
            <div className={`relative flex items-center space-x-2 cursor-pointer ${isDeleted ? 'opacity-40' : ''}`}>
                {renderProfileImage()}
                <div className="flex-1">
                    {renderPostTitle()}
                    {renderFooter('hidden lg:flex')}
                </div>
                {renderMainImage()}
            </div>
            {renderFooter('lg:hidden')}
            {renderPendingApproveOrDelete()}
        </Link>
    );
};

export default memo(PostCardCompact, propsEqualityFn);
